import { useEffect, useRef, useState } from 'react';

interface UseCountUpOptions {
  /** Final value. */
  end: number;
  /** Animation duration in ms. */
  duration?: number;
  /** Starting value. */
  start?: number;
  /** Decimal places in the returned value. */
  decimals?: number;
}

/**
 * Count-up animation for stat numbers, driven by requestAnimationFrame.
 * Respects prefers-reduced-motion (jumps straight to the end value).
 */
export function useCountUp({ end, duration = 800, start = 0, decimals = 0 }: UseCountUpOptions): number {
  const [value, setValue] = useState(start);
  const frameRef = useRef<number>();

  useEffect(() => {
    if (!Number.isFinite(end)) {
      setValue(0);
      return;
    }
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || duration <= 0) {
      setValue(end);
      return;
    }

    const startTime = performance.now();
    const factor = Math.pow(10, decimals);

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      // ease-out cubic — fast start, satisfying settle
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * eased;
      setValue(Math.round(current * factor) / factor);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [end, duration, start, decimals]);

  return value;
}

export default useCountUp;
